import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { App, Holding, Initiative } from '../lib/types';

export interface RoadmapState {
  holdings: Holding[];
  apps: App[];
  initiatives: Initiative[];
  loading: boolean;
  error: string | null;
}

export function useRoadmap(enabled: boolean) {
  const [state, setState] = useState<RoadmapState>({
    holdings: [],
    apps: [],
    initiatives: [],
    loading: true,
    error: null,
  });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    const [h, a, i] = await Promise.all([
      supabase.from('roadmap_holdings').select('*').order('position'),
      supabase.from('roadmap_apps').select('*').order('position'),
      supabase.from('roadmap_initiatives').select('*').order('position'),
    ]);
    const err = h.error || a.error || i.error;
    if (err) {
      setState({ holdings: [], apps: [], initiatives: [], loading: false, error: err.message });
      return;
    }
    setState({
      holdings: (h.data ?? []) as Holding[],
      apps: (a.data ?? []) as App[],
      initiatives: (i.data ?? []) as Initiative[],
      loading: false,
      error: null,
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();
  }, [enabled, refresh]);

  return { ...state, refresh };
}

export async function upsertInitiative(partial: Partial<Initiative> & { app_id: string; title: string }) {
  const { data, error } = await supabase
    .from('roadmap_initiatives')
    .upsert(partial)
    .select()
    .single();
  if (error) throw error;
  return data as Initiative;
}

export async function updateInitiativeStatus(id: string, status: Initiative['status']) {
  const patch: Partial<Initiative> = { status };
  if (status === 'done') patch.completed_at = new Date().toISOString();
  if (status !== 'done') patch.completed_at = null;
  const { error } = await supabase.from('roadmap_initiatives').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteInitiative(id: string) {
  const { error } = await supabase.from('roadmap_initiatives').delete().eq('id', id);
  if (error) throw error;
}

export async function upsertApp(partial: Partial<App> & { holding_id: string; slug: string; name: string }) {
  const { data, error } = await supabase
    .from('roadmap_apps')
    .upsert(partial, { onConflict: 'slug' })
    .select()
    .single();
  if (error) throw error;
  return data as App;
}
